<script setup lang="ts">
import BaseButton from "@/components/Base/Button.vue";
import {
  disposeBootstrapComponent,
  releaseBodyDialogLock,
} from "@/utils/bootstrap";
import { Modal } from "bootstrap";
import type { StyleValue } from "vue";

// inheritAttrs is the Vue default; declared explicitly because callers (e.g. AnnouncementModal) bind aria-labelledby and rely on it landing on the dialog root.
defineOptions({ name: "BaseModal", inheritAttrs: true });

export type Props = {
  open?: boolean;
  size?: "sm" | "md" | "lg" | "xl" | "fullscreen";
  centered?: boolean;
  maxWidth?: boolean;
  scrollable?: boolean;
  /**
   * With `scrollable`: keep Bootstrap from scrolling the whole body and lay it
   * out as a rigid flex column instead, so the slot content owns the scrolling
   * (e.g. an inner list pane scrolls while side rails stay anchored).
   */
  delegateScroll?: boolean;
  bottomAnchored?: boolean;
  rounded?: boolean;
  absolute?: boolean;
  coordinates?: { x: number; y: number };
  backdrop?: boolean | "static";
  keyboard?: boolean;
  embedded?: boolean;
  enableConfirmHide?: boolean;
  focus?: boolean;
  confirmHideFunc?: () => Promise<boolean>;
  title?: string;
  showCloseButton?: boolean;
  showFooter?: boolean;
  overflowVisible?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  size: "md",
  rounded: true,
  backdrop: true,
  keyboard: true,
  focus: true,
  coordinates: () => ({ x: 0, y: 0 }),
  showCloseButton: true,
  showFooter: true,
});

type Emits = {
  hide: [];
  show: [];
  hidden: [event: Event];
  "force-closed": [];
};

const emit = defineEmits<Emits>();

const { t } = useI18n();

let modal: Modal | null = null;
const modalElementRef = ref<HTMLElement | null>(null);
const { rememberOpener, restoreFocus } = useDialogFocusReturn(modalElementRef);

const toggleModal = (open: boolean): void => {
  if (open) {
    show();
  } else {
    hide();
  }
};

const show = (): void => {
  if (modal) {
    modal.show();
  }
};

const forceCloseModal = (): void => {
  if (!modalElementRef.value) {
    return;
  }
  console.warn(
    "[Modal] Bootstrap modal failed to hide, using fallback cleanup"
  );

  // Force remove modal state and styling
  modalElementRef.value.classList.remove("show");
  modalElementRef.value.style.display = "none";
  modalElementRef.value.setAttribute("aria-hidden", "true");
  modalElementRef.value.removeAttribute("aria-modal");

  // `show` is gone from this element by now, so nothing counts it as open.
  releaseBodyDialogLock();
  // No `hidden.bs.modal` follows a forced close, so focus is returned here.
  restoreFocus();

  // Emit event to notify parent components
  emit("force-closed");
};

const MODAL_FALLBACK_DELAY_MS = 150;
let hideFallbackTimeout: ReturnType<typeof setTimeout> | undefined;

const hide = (): void => {
  if (modal && modalElementRef.value) {
    try {
      modal.hide();

      // Fallback: Force close if Bootstrap modal doesn't respond within timeout
      hideFallbackTimeout = setTimeout(() => {
        hideFallbackTimeout = undefined;
        if (modalElementRef.value?.classList.contains("show")) {
          forceCloseModal();
        }
      }, MODAL_FALLBACK_DELAY_MS);
    } catch (error) {
      console.warn("[Modal] Modal hide failed:", error);
      forceCloseModal();
    }
  }
};

watch(
  () => props.open,
  (newValue: boolean) => {
    toggleModal(newValue);
  }
);

const isBlocked = ref(true);

const init = (): void => {
  if (modalElementRef.value) {
    modal = new Modal(modalElementRef.value, {
      backdrop: props.backdrop,
      keyboard: props.keyboard,
      focus: props.focus,
    });

    modalElementRef.value.addEventListener("hide.bs.modal", (event) => {
      if (props.enableConfirmHide) {
        if (isBlocked.value) {
          event.preventDefault();

          void (async () => {
            const isConfirmed = await props.confirmHideFunc?.();

            if (isConfirmed) {
              isBlocked.value = false;
              modal?.hide();
              emit("hide");
            }
          })();
        }
      } else {
        emit("hide");
      }
    });

    // A confirmed hide unblocks the modal; re-arm it on every open, or a modal
    // that stays mounted between opens would skip the confirm from then on.
    modalElementRef.value.addEventListener("show.bs.modal", () => {
      isBlocked.value = true;
      rememberOpener();
    });

    // Focus goes back before `hidden` is emitted, so a consumer that places
    // focus itself in its handler has the last word.
    modalElementRef.value.addEventListener("hidden.bs.modal", (event) => {
      restoreFocus();
      emit("hidden", event);
    });

    modalElementRef.value.addEventListener("shown.bs.modal", function () {
      emit("show");
    });
  }
};

onMounted(() => {
  init();
  if (props.open) {
    show();
  }
});

onBeforeUnmount(() => {
  clearTimeout(hideFallbackTimeout);
  // Flush any in-flight hide transition before dispose so Bootstrap's queued
  // completion callback runs while _element is still valid — otherwise the
  // setTimeout fallback fires transitionend against a null _element and throws
  // "Cannot read properties of null (reading 'style')" (Sentry FOBIZZ-TOOLS-5K1).
  disposeBootstrapComponent(modalElementRef.value, modal);
});

// Safety net for v-if teardown — after, not before, unmount. Two dialogs torn
// down together (the share off-canvas and the classroom modal on top of it,
// when a link inside navigates away) each still see the other in the DOM at
// beforeUnmount, and both keep their hands off the body. Once the elements are
// gone, the last one out releases the lock.
onUnmounted(() => {
  releaseBodyDialogLock();
});

const modalDialogClasses = computed(() => {
  if (props.size === "fullscreen") {
    return `modal-${props.size}`;
  } else {
    return [
      // Default mobile behavior is fullscreen, but bottomAnchored opts out so
      // the dialog can hug the viewport bottom and size to its content on phones too.
      { "modal-fullscreen-sm-down": !props.bottomAnchored },
      `modal-${props.size}`,
      {
        "modal-dialog-centered": props.centered,
        "modal-dialog--max-width": props.maxWidth,
        "modal-dialog-scrollable": props.scrollable,
        "modal-dialog--bottom": props.bottomAnchored,
        "modal-dialog--rounded": props.rounded,
        "modal-dialog--embedded": props.embedded,
      },
    ];
  }
});

const modalStyles = computed<StyleValue>(() => {
  if (props.absolute) {
    return {
      position: "absolute",
      top: `${props.coordinates.y}px`,
      left: 0,
      right: 0,
    };
  }

  return {};
});

const showDefaultHeader = computed(() => {
  return !!props.title || props.showCloseButton;
});

// Check if raw slot is provided to bypass default structure
const slots = useSlots();
const hasRawSlot = computed(() => !!slots.raw);

defineExpose({ show, hide });
</script>

<template>
  <div
    ref="modalElementRef"
    class="modal fade"
    tabindex="-1"
    data-testid="modal"
    :aria-label="title"
  >
    <div class="modal-dialog" :class="modalDialogClasses">
      <div class="modal-content" :style="modalStyles">
        <!-- Raw slot bypasses all default structure -->
        <slot v-if="hasRawSlot" name="raw"></slot>

        <!-- Default structure -->
        <template v-else>
          <slot name="header">
            <div v-if="showDefaultHeader" class="modal-header">
              <h5 v-if="title" class="modal-title">{{ title }}</h5>
              <BaseButton
                v-if="showCloseButton"
                kind="tertiary"
                class="modal-header__close"
                icon-name="x-lg"
                icon-only
                :aria-label="t('buttons.close')"
                data-bs-dismiss="modal"
              />
            </div>
          </slot>

          <div
            class="modal-body"
            :class="{
              'overflow-visible': overflowVisible,
              'modal-body--delegate-scroll': delegateScroll,
            }"
          >
            <slot></slot>
          </div>

          <div v-if="showFooter" class="modal-footer">
            <slot name="footer">
              <BaseButton kind="primary" data-bs-dismiss="modal" type="button">
                {{ t("links.close") }}
              </BaseButton>
            </slot>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.modal-content {
  box-shadow: $brand-box-shadow;
  background-color: $color-surface-white;
  border: 1px solid $color-secondary;
  border-radius: $border-radius;
}

.modal--dark {
  .modal-content {
    background-color: transparent;
  }
}

.modal-dialog--max-width {
  max-width: 720px;
  min-height: 400px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: $spacer-3;
  height: $modal-header-height;
  border-radius: 0;
  border-bottom: none;
  padding: $spacer-3 $spacer-4;
}

// Keeps the X top-right when a dialog has no title and renders its own
// heading in the body (#13280); with a title, `.modal-title` already fills.
.modal-header__close {
  margin-left: auto;
}

// Truncate long titles to a single line; the header has fixed height (70px)
// so wrapping would clip or overflow. Opt into multi-line via .modal-title--cropped.
.modal-title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// Nested under .modal-dialog-scrollable both because the mode only means
// something there and because the extra specificity beats Bootstrap's
// `.modal-dialog-scrollable .modal-body { overflow-y: auto }` regardless of
// stylesheet order.
.modal-dialog-scrollable .modal-body--delegate-scroll {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.modal-body {
  padding: 0 $spacer-4 $spacer-4;

  h5 {
    font-size: 1em;
  }

  .modal-list {
    list-style: none;
    padding-left: 1.75rem;

    li {
      margin: 0.25rem 0;
    }

    i {
      display: inline-block;
      width: 1.75rem;
      margin-left: -1.75rem;
    }
  }

  &.overflow-visible {
    overflow: visible;
  }
}

.modal-footer {
  background-color: $border-color-light;
  border-top: none;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  padding: $spacer-3 $spacer-4;
}

.modal-dialog--rounded .modal-content {
  border-radius: $border-radius;
  overflow: hidden;

  &:has(.overflow-visible) {
    overflow: visible;
  }
}

// Bottom-anchored variant — mobile-only opt-in bottom sheet. On phones the
// dialog becomes a full-width sheet flush with the viewport bottom, takes a
// fixed share of the viewport height so its content scrolls inside rather than
// resizing it, rounds only its top corners, and slides up from below on enter.
// At sm+ the class is a no-op so desktop uses Bootstrap's default top-anchored
// modal.
@include media-breakpoint-down(sm) {
  .modal-dialog--bottom {
    display: flex;
    align-items: flex-end;
    width: 100vw;
    max-width: none;
    margin: 0;
    min-height: 100%;

    .modal-content {
      // A fixed height, per the design's 738px of an 844px viewport. The sheet
      // used to size to its content, which made it jump on every tab change:
      // the picker's header gains ~60px when the tab is searchable, and the
      // tabs' natural heights run from a 175px drop zone to a viewport-tall
      // image grid. A sheet that does not resize cannot jump, and the body
      // already scrolls inside it via `.modal-body--delegate-scroll` (#12087).
      height: 87vh;
      max-height: 100%;

      // `$border-radius` (4px) reads as square on a sheet spanning 100vw.
      // Logical properties so the top corners stay the top corners in RTL.
      border-start-start-radius: $border-radius-xl;
      border-start-end-radius: $border-radius-xl;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      transition: transform $default-transition-speed ease-out;

      @media (prefers-reduced-motion: reduce) {
        transition: none;
      }
    }
  }

  // Cancel Bootstrap's default top-down slide on the dialog so it doesn't
  // animate alongside the content slide. Matches Bootstrap's specificity.
  .modal.fade .modal-dialog--bottom,
  .modal.show .modal-dialog--bottom {
    transform: none;
  }

  .modal.fade .modal-dialog--bottom .modal-content {
    transform: translateY(100%);
  }

  .modal.show .modal-dialog--bottom .modal-content {
    transform: translateY(0);
  }
}

.modal-dialog {
  &:has(.overflow-visible) {
    overflow: visible;
  }
}

.modal-dialog--absolute {
  position: absolute;
  @include media-breakpoint-down(md) {
    position: relative;
  }
}

// text-editor modal
.modal-dialog--embedded {
  --bs-modal-width: 700px;

  /* stylelint-disable-next-line no-descending-specificity */
  .modal-content {
    border: 2px solid $border-color;
    border-radius: $border-radius-lg;
    box-shadow: $brand-box-shadow;
    background-color: $color-surface-white;
    max-height: 400px;

    @include media-breakpoint-down(md) {
      max-height: 100%;
    }
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    height: auto;
  }

  .modal-title {
    font-size: 1rem;
  }

  .modal-footer {
    background-color: $color-surface-white;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}

// survey question_answers details modal — opt-in multi-line title.
// Overrides the default single-line truncation on .modal-title.
.modal-title--cropped {
  display: -webkit-box;
  overflow: hidden;
  white-space: normal;
  text-overflow: clip;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  margin-top: $spacer-3;
  padding-bottom: 0.25rem;
}
</style>
